import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/useToast";
import PropTypes from "prop-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FaGoogleDrive } from "react-icons/fa";

// Google Drive API constants - Using import.meta.env for Vite
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || "";
// The APP_ID should be your Google Cloud project ID or project number
const APP_ID =
  import.meta.env.VITE_GOOGLE_PROJECT_NUMBER ||
  import.meta.env.VITE_GOOGLE_PROJECT_ID ||
  "967800842825";

// Authorization scopes required by the API
const SCOPES = "https://www.googleapis.com/auth/drive.file";

/**
 * GoogleDriveButton component for selecting files from Google Drive
 * Following the official Google Picker API documentation pattern
 * Reference: https://developers.google.com/workspace/drive/picker/guides/overview
 */
const GoogleDriveButton = forwardRef(
  (
    { onFilesSelected, onError, disabled = false, className, children, id },
    ref
  ) => {
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const callbackRef = useRef(null);
    const tokenClientRef = useRef(null);
    const accessTokenRef = useRef(null);
    const apiCallbackRef = useRef(null);

    // Store the callback for this instance
    useEffect(() => {
      callbackRef.current = onFilesSelected;
    }, [onFilesSelected]);

    /**
     * Initialize the token client when GIS is ready
     */
    const initializeTokenClient = useCallback(() => {
      if (!window.google?.accounts?.oauth2) {
        console.error("Google OAuth2 API not available");
        return;
      }

      try {
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPES,
          callback: "", // Will be set when requesting token
        });
      } catch (error) {
        // Failed to initialize Google authentication
      }
    }, [toast]);

    /**
     * Check if both APIs are ready and enable the button
     */
    const checkReadyState = useCallback(() => {
      const pickerReady =
        window.googleApiState?.pickerInited && window.google?.picker;
      const gisReady = tokenClientRef.current !== null;

      if (pickerReady && gisReady) {
        setIsReady(true);
      }
    }, []);

    /**
     * Initialize the Drive API client
     */
    const initializeDriveClient = useCallback(async () => {
      try {
        // First ensure gapi.client is initialized
        if (!window.gapi?.client) {
          await new Promise((resolve, reject) => {
            window.gapi.load("client", {
              callback: async () => {
                try {
                  // Initialize the client with API key only (no discovery docs)
                  await window.gapi.client.init({
                    apiKey: API_KEY,
                  });
                  resolve();
                } catch (error) {
                  reject(error);
                }
              },
              onerror: (error) => {
                reject(error);
              },
            });
          });
        }

        // For Google Picker, we don't actually need the Drive API client
        // The picker handles file access internally with the OAuth token
        checkReadyState();
      } catch (error) {
        // Don't show error toast for API issues - picker can still work
        checkReadyState();
      }
    }, [checkReadyState]);

    /**
     * Callback when picker API is ready
     */
    const onPickerReady = useCallback(() => {
      initializeDriveClient();
    }, [initializeDriveClient]);

    /**
     * Callback when Google Identity Services is ready
     */
    const onGisReady = useCallback(() => {
      initializeTokenClient();
      checkReadyState();
    }, [initializeTokenClient, checkReadyState]);

    /**
     * Picker callback to handle user selections
     * Following the official documentation pattern
     */
    const pickerCallback = useCallback(
      async (data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          try {
            const documents = data[window.google.picker.Response.DOCUMENTS];

            if (!documents || documents.length === 0) {
              setIsLoading(false);
              return;
            }

            // Processing files

            const files = [];

            // Process each selected document and download content
            for (const document of documents) {
              try {
                const fileId = document[window.google.picker.Document.ID];
                const fileName = document[window.google.picker.Document.NAME];
                const mimeType =
                  document[window.google.picker.Document.MIME_TYPE];

                // Download the actual file content
                const downloadedFile = await downloadGoogleDriveFileContent(
                  {
                    id: fileId,
                    name: fileName,
                    mimeType: mimeType,
                  },
                  accessTokenRef.current
                );

                if (downloadedFile) {
                  // Check if this is a fallback file (metadata or error file)
                  const isFallbackFile =
                    downloadedFile.name.includes("_metadata.json") ||
                    downloadedFile.name.includes("_error.txt");

                  files.push({
                    id: fileId,
                    name: downloadedFile.name,
                    mimeType: downloadedFile.type,
                    url: document[window.google.picker.Document.URL],
                    iconUrl: document[window.google.picker.Document.ICON_URL],
                    sizeBytes: downloadedFile.size,
                    lastEditedUtc:
                      document[window.google.picker.Document.LAST_EDITED_UTC],
                    description:
                      document[window.google.picker.Document.DESCRIPTION] || "",
                    type: "google-drive",
                    source: "Google Drive",
                    fileObject: downloadedFile, // The actual File object with content
                    downloadSuccess: true,
                    originalGoogleMimeType: mimeType,
                    originalFileName: fileName, // Keep track of original name
                    isFallbackFile: isFallbackFile, // Flag to indicate this is a fallback
                  });

                  // File processed successfully
                } else {
                  // Fallback for failed downloads
                  files.push({
                    id: fileId,
                    name: fileName,
                    mimeType: mimeType,
                    url: document[window.google.picker.Document.URL],
                    iconUrl: document[window.google.picker.Document.ICON_URL],
                    sizeBytes:
                      document[window.google.picker.Document.SIZE_BYTES] || 0,
                    lastEditedUtc:
                      document[window.google.picker.Document.LAST_EDITED_UTC],
                    description:
                      document[window.google.picker.Document.DESCRIPTION] || "",
                    type: "google-drive",
                    source: "Google Drive",
                    downloadSuccess: false,
                    error: "Failed to download file content",
                  });
                }
              } catch (fileError) {
                // Error processing file

                // Add failed file to results
                files.push({
                  id: document[window.google.picker.Document.ID],
                  name: document[window.google.picker.Document.NAME],
                  mimeType: document[window.google.picker.Document.MIME_TYPE],
                  url: document[window.google.picker.Document.URL],
                  iconUrl: document[window.google.picker.Document.ICON_URL],
                  sizeBytes:
                    document[window.google.picker.Document.SIZE_BYTES] || 0,
                  type: "google-drive",
                  source: "Google Drive",
                  downloadSuccess: false,
                  error: fileError.message,
                });
              }
            }

            // Filter successful downloads
            const successfulFiles = files.filter((f) => f.downloadSuccess);
            const fallbackFiles = successfulFiles.filter(
              (f) => f.isFallbackFile
            );
            const actualFiles = successfulFiles.filter(
              (f) => !f.isFallbackFile
            );

            if (successfulFiles.length === 0) {
              setIsLoading(false);
              return;
            }

            if (callbackRef.current) {
              callbackRef.current(successfulFiles);
            }
          } catch (error) {
            if (onError) onError("Failed to process selected files");
          }
        } else if (data.action === window.google.picker.Action.CANCEL) {
          // User cancelled the picker - no action needed
        } else {
          // Other picker action - no action needed
        }

        setIsLoading(false);
      },
      [toast, onError]
    );

    // Helper function to download Google Drive file content
    const downloadGoogleDriveFileContent = async (file, accessToken) => {
      try {
        let response;
        let finalMimeType = file.mimeType;
        let finalName = file.name;

        // Check if this is a Google Workspace file that needs to be exported
        if (file.mimeType && file.mimeType.includes("google-apps")) {
          const exportMimeType = getExportMimeType(file.mimeType);
          finalMimeType = exportMimeType;

          // Update file extension based on export type
          if (exportMimeType.includes("wordprocessingml")) {
            finalName = file.name.replace(/\.[^/.]+$/, "") + ".docx";
          } else if (exportMimeType === "text/csv") {
            finalName = file.name.replace(/\.[^/.]+$/, "") + ".csv";
          } else if (exportMimeType.includes("presentationml")) {
            finalName = file.name.replace(/\.[^/.]+$/, "") + ".pptx";
          } else if (exportMimeType === "image/png") {
            finalName = file.name.replace(/\.[^/.]+$/, "") + ".png";
          } else {
            finalName = file.name.replace(/\.[^/.]+$/, "") + ".pdf";
          }

          // Exporting Google Workspace file

          // Try export first with shared drive support
          response = await fetch(
            `https://www.googleapis.com/drive/v3/files/${file.id}/export?mimeType=${encodeURIComponent(exportMimeType)}&supportsAllDrives=true&includeItemsFromAllDrives=true`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          // If export fails with 404, try getting file metadata first to verify access
          if (!response.ok && response.status === 404) {
            // Try to get file metadata to verify we have access with shared drive support
            const metadataResponse = await fetch(
              `https://www.googleapis.com/drive/v3/files/${file.id}?fields=id,name,mimeType,size,permissions&supportsAllDrives=true&includeItemsFromAllDrives=true`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (metadataResponse.ok) {
              const metadata = await metadataResponse.json();

              // If we can access metadata but not export, create a metadata file
              const metadataContent = JSON.stringify(
                {
                  name: file.name,
                  id: file.id,
                  mimeType: file.mimeType,
                  exportMimeType: exportMimeType,
                  size: metadata.size,
                  source: "Google Drive",
                  note: "File content could not be exported directly. This is a metadata representation.",
                  webViewLink: `https://drive.google.com/file/d/${file.id}/view`,
                  permissions: metadata.permissions,
                },
                null,
                2
              );

              const metadataBlob = new Blob([metadataContent], {
                type: "application/json",
              });
              const metadataFile = new File(
                [metadataBlob],
                `${file.name}_metadata.json`,
                {
                  type: "application/json",
                  lastModified: Date.now(),
                }
              );

              return metadataFile;
            } else {
              throw new Error(
                `File not accessible: ${response.status} ${response.statusText}`
              );
            }
          }
        } else {
          // For regular files, use download endpoint with shared drive support

          response = await fetch(
            `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media&supportsAllDrives=true&includeItemsFromAllDrives=true`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          // If regular download fails with 404, try metadata approach
          if (!response.ok && response.status === 404) {
            const metadataResponse = await fetch(
              `https://www.googleapis.com/drive/v3/files/${file.id}?fields=id,name,mimeType,size,webViewLink&supportsAllDrives=true&includeItemsFromAllDrives=true`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (metadataResponse.ok) {
              const metadata = await metadataResponse.json();

              // Create a metadata file as fallback
              const metadataContent = JSON.stringify(
                {
                  name: file.name,
                  id: file.id,
                  mimeType: file.mimeType,
                  size: metadata.size,
                  source: "Google Drive",
                  note: "File content could not be downloaded directly. This is a metadata representation.",
                  webViewLink:
                    metadata.webViewLink ||
                    `https://drive.google.com/file/d/${file.id}/view`,
                },
                null,
                2
              );

              const metadataBlob = new Blob([metadataContent], {
                type: "application/json",
              });
              const metadataFile = new File(
                [metadataBlob],
                `${file.name}_metadata.json`,
                {
                  type: "application/json",
                  lastModified: Date.now(),
                }
              );

              return metadataFile;
            } else {
              throw new Error(
                `File not accessible: ${response.status} ${response.statusText}`
              );
            }
          }
        }

        if (!response.ok) {
          const errorText = await response.text();

          // For other errors (403, 429, etc.), still try metadata approach
          if (response.status === 403 || response.status === 429) {
            // Trying metadata fallback

            try {
              const metadataResponse = await fetch(
                `https://www.googleapis.com/drive/v3/files/${file.id}?fields=id,name,mimeType,size,webViewLink`,
                {
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                  },
                }
              );

              if (metadataResponse.ok) {
                const metadata = await metadataResponse.json();

                const metadataContent = JSON.stringify(
                  {
                    name: file.name,
                    id: file.id,
                    mimeType: file.mimeType,
                    size: metadata.size,
                    source: "Google Drive",
                    note: `File content could not be accessed (${response.status}). This is a metadata representation.`,
                    webViewLink:
                      metadata.webViewLink ||
                      `https://drive.google.com/file/d/${file.id}/view`,
                    error: `HTTP ${response.status}: ${response.statusText}`,
                  },
                  null,
                  2
                );

                const metadataBlob = new Blob([metadataContent], {
                  type: "application/json",
                });
                const metadataFile = new File(
                  [metadataBlob],
                  `${file.name}_metadata.json`,
                  {
                    type: "application/json",
                    lastModified: Date.now(),
                  }
                );

                return metadataFile;
              }
            } catch (metadataError) {
              // Metadata fallback failed
            }
          }

          throw new Error(
            `Failed to download file: ${response.status} ${response.statusText}`
          );
        }

        const blob = await response.blob();

        // Create File object with the downloaded content
        const fileObj = new File([blob], finalName, {
          type: finalMimeType,
          lastModified: Date.now(),
        });

        return fileObj;
      } catch (error) {
        // Failed to download Google Drive file

        // As a last resort, create a simple text file with the error and file info
        const errorContent = `Google Drive File: ${file.name}
File ID: ${file.id}
MIME Type: ${file.mimeType}
Source: Google Drive
Error: ${error.message}

This file could not be downloaded from Google Drive.
You can access it directly at: https://drive.google.com/file/d/${file.id}/view

Note: This may be due to file permissions, sharing settings, or the file being moved/deleted.`;

        const errorBlob = new Blob([errorContent], { type: "text/plain" });
        const errorFile = new File([errorBlob], `${file.name}_error.txt`, {
          type: "text/plain",
          lastModified: Date.now(),
        });

        return errorFile;
      }
    };

    // Helper function to get appropriate export MIME type for Google Workspace files
    const getExportMimeType = (googleMimeType) => {
      const exportMap = {
        "application/vnd.google-apps.document":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        // Note: Google Sheets are not supported - export as CSV instead
        "application/vnd.google-apps.spreadsheet": "text/csv",
        "application/vnd.google-apps.presentation":
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.google-apps.drawing": "image/png",
        "application/vnd.google-apps.script":
          "application/vnd.google-apps.script+json",
      };

      return exportMap[googleMimeType] || "application/pdf";
    };

    /**
     * Create and display the Google Picker
     * Following the official documentation pattern
     */
    const createPicker = useCallback(() => {
      // Validate that all required APIs are available
      if (!window.google?.picker) {
        setIsLoading(false);
        if (onError) onError("Google Picker API is not available");
        return;
      }

      if (!accessTokenRef.current) {
        setIsLoading(false);
        if (onError) onError("No access token available");
        return;
      }

      try {
        // Create a view for documents (you can customize this)
        const docsView = new window.google.picker.DocsView(
          window.google.picker.ViewId.DOCS
        ).setIncludeFolders(true);

        // Create an upload view
        const uploadView = new window.google.picker.DocsUploadView();

        // Build the picker with multiple views
        const picker = new window.google.picker.PickerBuilder()
          .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
          .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
          .setDeveloperKey(API_KEY)
          .setAppId(APP_ID)
          .setOAuthToken(accessTokenRef.current)
          .addView(docsView)
          .addView(uploadView)
          .setCallback(pickerCallback)
          .build();

        picker.setVisible(true);
      } catch (error) {
        setIsLoading(false);
        if (onError)
          onError(`Failed to create Google Drive picker: ${error.message}`);
      }
    }, [pickerCallback, toast, onError]);

    /**
     * Handle authentication and show picker
     * Following the official documentation pattern
     */
    const handleAuthClick = useCallback(() => {
      if (!isReady || isLoading || !tokenClientRef.current) {
        return;
      }

      setIsLoading(true);

      // Set the callback for this authentication request
      tokenClientRef.current.callback = async (response) => {
        if (response.error !== undefined) {
          setIsLoading(false);
          if (onError) onError(`Failed to authenticate: ${response.error}`);
          return;
        }

        accessTokenRef.current = response.access_token;

        // Store the access token in sessionStorage so ChatInput can access it for file downloads
        try {
          sessionStorage.setItem(
            "google_drive_access_token",
            response.access_token
          );
        } catch (e) {
          // Could not store access token in sessionStorage
        }

        // Also store in global state if available
        if (window.googleApiState) {
          window.googleApiState.accessToken = response.access_token;
        }

        createPicker();
      };

      // Request access token
      if (accessTokenRef.current === null) {
        tokenClientRef.current.requestAccessToken({ prompt: "consent" });
      } else {
        tokenClientRef.current.requestAccessToken({ prompt: "" });
      }
    }, [isReady, isLoading, createPicker, toast, onError]);

    /**
     * Expose the openPicker method via ref
     */
    useImperativeHandle(
      ref,
      () => ({
        openPicker: () => {
          if (!isReady) {
            if (onError) onError("Google Drive picker is not ready yet");
            return;
          }

          setIsLoading(true);
          handleAuthClick();
        },
        isReady,
        isLoading,
      }),
      [isReady, handleAuthClick, isLoading, toast, onError]
    );

    // Register with the global Google API state management
    useEffect(() => {
      if (!CLIENT_ID || !API_KEY) {
        return;
      }

      // Create callback object for registration
      apiCallbackRef.current = {
        onPickerReady,
        onGisReady,
      };

      // Register with global API state
      if (window.registerGoogleApiCallback) {
        window.registerGoogleApiCallback(apiCallbackRef.current);
      } else {
        // Try to initialize directly if APIs are already loaded
        if (window.googleApiState?.pickerInited) {
          onPickerReady();
        }
        if (window.googleApiState?.gisInited) {
          onGisReady();
        }
      }

      // Cleanup function
      return () => {
        if (window.unregisterGoogleApiCallback && apiCallbackRef.current) {
          window.unregisterGoogleApiCallback(apiCallbackRef.current);
        }
      };
    }, [onPickerReady, onGisReady, toast]);

    // Don't render if not configured
    if (!CLIENT_ID || !API_KEY) {
      return null;
    }

    return (
      <Button
        ref={ref}
        id={id}
        variant="outline"
        size="sm"
        onClick={(e) => {
          handleAuthClick();
        }}
        disabled={disabled || isLoading || !isReady}
        className={`flex items-center gap-2 ${className}`}
        style={{ position: "relative", zIndex: 10 }}
      >
        <FaGoogleDrive
          className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
        />
        {isLoading ? "Loading..." : children}
      </Button>
    );
  }
);

GoogleDriveButton.displayName = "GoogleDriveButton";

GoogleDriveButton.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  id: PropTypes.string,
};

export default GoogleDriveButton;
